import { Box } from '@atlaskit/primitives';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseField } from '@databases/types/database';
import { type DatabaseCalculationType } from '@databases/types/database/view';
import useView from '@databases/views/useView';
import React, { forwardRef, useCallback, useMemo } from 'react';

import { QuickCalcOptions } from './QuickCalcOptions';
import { handleCalculationSelectionChange } from './quickCalcUtils';
import { type CalculationSelectOption } from './types';

type DatabaseQuickCalcValueEditProps = {
  field: DatabaseField;
  contentId: string;
  onClose: () => void;
};

const DatabaseQuickCalcValueEdit = forwardRef<
  HTMLInputElement,
  DatabaseQuickCalcValueEditProps
>(function DatabaseValueBulkEdit({ contentId, field, onClose }, ref) {
  const { view } = useView();
  const analyticsClient = useAnalytics();
  const dispatch = defaultStoreUtils.useDispatch();

  const handleChange = useCallback(
    (option: CalculationSelectOption | null) => {
      handleCalculationSelectionChange(option, contentId, dispatch, field.id, view.id);
    },
    [contentId, dispatch, field.id, view.id]
  );

  const calculationType = useMemo(() => {
    return view.settings.calculations?.[field.id];
  }, [field.id, view.settings.calculations]);

  const handleOnClick = (databaseCalculationType?: DatabaseCalculationType) => {
    if (databaseCalculationType) {
      handleChange({ value: databaseCalculationType, label: databaseCalculationType });
      if (calculationType && calculationType !== databaseCalculationType) {
        analyticsClient.sendCalculationEventConverted({
          databaseContainer: getDatabaseContainerType(),
          fieldId: field.id,
          fieldType: field.type,
          fromCalculationType: calculationType,
          toCalculationType: databaseCalculationType,
          source: 'quickCalcFieldActions',
          contentId: contentId,
        });
      } else {
        analyticsClient.sendCreateCalculationEvent({
          databaseContainer: getDatabaseContainerType(),
          fieldId: field.id,
          fieldType: field.type,
          calculationType: databaseCalculationType,
          source: 'quickCalcFieldActions',
          contentId: contentId,
        });
      }
    } else {
      handleChange(null);
      analyticsClient.sendRemoveCalculationEvent({
        databaseContainer: getDatabaseContainerType(),
        fieldId: field.id,
        fieldType: field.type,
        source: 'quickCalcFieldActions',
        contentId: contentId,
      });
    }
    onClose();
  };

  return (
    <Box ref={ref} paddingBlock="space.075">
      <QuickCalcOptions
        field={field}
        handleOnClick={handleOnClick}
        calculations={view.settings.calculations}
        contentId={contentId}
      />
    </Box>
  );
});

export default DatabaseQuickCalcValueEdit;
